import { createContext, useContext, useState, useCallback } from "react";
import { toEthiopian, ETHIOPIAN_MONTHS } from "helper/calendarConversion";

const LOCALE_STORAGE_KEY = "askuala_locale";

const GREGORIAN_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Add a new locale key here to support a new country. No other file needs to change.
const LOCALE_CONFIGS = {
  et: {
    key: "et",
    country: "Ethiopia",
    flag: "🇪🇹",
    currency: { code: "ETB", symbol: "ብር", position: "after" },
    currencyOptions: [
      { value: "ETB", label: "ETB - Ethiopian Birr" },
      { value: "USD", label: "USD - US Dollar" },
    ],
    calendar: "ethiopian",
    exportLanguage: "amharic", // default for exam/analytics PDF & Excel exports
    documentLanguage: "both", // default for report cards & certificates
    documentLanguages: ["amharic", "english", "both"], // selectable certificate languages
    months: ETHIOPIAN_MONTHS,
    weekDays: ["እሁድ", "ሰኞ", "ማክሰኞ", "ረቡዕ", "ሐሙስ", "ዓርብ", "ቅዳሜ"], // Sun→Sat, matches JS getDay()
    absenceTemplate: (name, days, school) =>
      `ተማሪ ${name} በተደጋጋሚ ለ${days} ቀን ከት/ት ገበታው ስላልተገኘ ት/ቤት በመምጣት እንድታናግሩን ስንል በትህተና እናሳስባለን።\n${school}`,
  },
  gh: {
    key: "gh",
    country: "Ghana",
    flag: "🇬🇭",
    currency: { code: "GHS", symbol: "₵", position: "before" },
    currencyOptions: [
      { value: "GHS", label: "GHS - Ghanaian Cedi" },
      { value: "USD", label: "USD - US Dollar" },
    ],
    calendar: "gregorian",
    exportLanguage: "english",
    documentLanguage: "en",
    documentLanguages: ["english"],
    months: GREGORIAN_MONTHS,
    weekDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], // Sun→Sat, matches JS getDay()
    absenceTemplate: (name, days, school) =>
      `Dear parent/guardian, ${name} has been absent for ${days} consecutive day(s). Kindly visit the school at your earliest convenience.\n${school}`,
  },
};

function detectLocale() {
  const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (saved && LOCALE_CONFIGS[saved]) return saved;
  return window.location.hostname.includes("gh") ? "gh" : "et";
}

// ─── Pure helpers — safe to call outside React ───────────────────────────────

export function formatDate(isoDate, localeKey) {
  if (!isoDate) return "";
  const config = LOCALE_CONFIGS[localeKey] ?? LOCALE_CONFIGS.et;
  const normalized = String(isoDate).split("T")[0];

  if (config.calendar === "ethiopian") {
    const eth = toEthiopian(normalized);
    return `${eth.day} ${config.months[eth.month]} ${eth.year}`;
  }

  const [y, m, d] = normalized.split("-");
  return `${d}/${m}/${y}`;
}

export function formatCurrency(amount, localeKey) {
  if (amount === null || amount === undefined || amount === "") return "";
  const config = LOCALE_CONFIGS[localeKey] ?? LOCALE_CONFIGS.et;
  const num = Number(amount).toLocaleString();
  return config.currency.position === "after"
    ? `${num} ${config.currency.symbol}`
    : `${config.currency.symbol}${num}`;
}

export function getAbsenceTemplate(studentFirstName, absenceDays, schoolName, localeKey) {
  const config = LOCALE_CONFIGS[localeKey] ?? LOCALE_CONFIGS.et;
  return config.absenceTemplate(studentFirstName, absenceDays, schoolName);
}

export function getLocaleConfig(localeKey) {
  return LOCALE_CONFIGS[localeKey] ?? LOCALE_CONFIGS.et;
}

// Returns "ጥቅምት 2017" for ET or "October 2025" for GH, given a Gregorian year+month.
// Uses the 15th of the month as the reference point so the dominant EC month is shown.
export function formatCalendarMonth(gregYear, gregMonth, localeKey) {
  const config = LOCALE_CONFIGS[localeKey] ?? LOCALE_CONFIGS.et;
  if (config.calendar === "ethiopian") {
    const mid = `${gregYear}-${String(gregMonth).padStart(2, "0")}-15`;
    const eth = toEthiopian(mid);
    return `${config.months[eth.month]} ${eth.year}`;
  }
  return new Date(gregYear, gregMonth - 1).toLocaleString("default", {
    month: "long",
    year: "numeric",
  });
}

// Returns a full locale-aware date string with weekday, e.g. "ሰኞ, 15 ጥቅምት 2017".
export function formatDateFull(isoDate, localeKey) {
  if (!isoDate) return "";
  const config = LOCALE_CONFIGS[localeKey] ?? LOCALE_CONFIGS.et;
  const normalized = String(isoDate).split("T")[0];
  const [y, m, d] = normalized.split("-").map(Number);
  const gregDate = new Date(y, m - 1, d);
  const weekDay = config.weekDays[gregDate.getDay()];

  if (config.calendar === "ethiopian") {
    const eth = toEthiopian(normalized);
    return `${weekDay}, ${eth.day} ${config.months[eth.month]} ${eth.year}`;
  }
  return gregDate.toLocaleDateString("default", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// All supported locales as a flat list — use to render a locale switcher UI.
export const SUPPORTED_LOCALES = Object.values(LOCALE_CONFIGS).map(({ key, country, flag }) => ({
  key,
  country,
  flag,
}));

// ─── React context ────────────────────────────────────────────────────────────

const LocaleContext = createContext(null);

// eslint-disable-next-line react/prop-types
export function LocaleProvider({ children }) {
  const [localeKey, setLocaleKeyState] = useState(detectLocale);

  const setLocale = useCallback((key) => {
    if (!LOCALE_CONFIGS[key]) return;
    localStorage.setItem(LOCALE_STORAGE_KEY, key);
    setLocaleKeyState(key);
  }, []);

  const value = {
    localeKey,
    locale: LOCALE_CONFIGS[localeKey],
    setLocale,
    // Bound helpers — no need to pass localeKey at the call site
    formatDate: (isoDate) => formatDate(isoDate, localeKey),
    formatCurrency: (amount) => formatCurrency(amount, localeKey),
    getAbsenceTemplate: (name, days, school) => getAbsenceTemplate(name, days, school, localeKey),
  };

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used inside <LocaleProvider>");
  return ctx;
}
